import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons/faExclamationTriangle';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';

import React, { useState, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import classnames from 'classnames';

import { Checkbox } from 'ui-components/components/checkbox';
import { Button } from 'ui-components/components/button';
import { noop } from 'ui-components/default-props/noop';
import { TruncatedText } from 'ui-components/components/truncated-text';

import { MessageDescriptorPropType } from '../../../../../prop-types/intl';
import Global from '../../../../../store/global-selectors';
import {
  openShipmentDetailsFromLabelBatchStatus,
  openShipmentDetailsFromLabelBatchStatusProcessing,
} from '../../../../../store/shipments/shipment-details/shipments.details.actions';
import { goToOrderDetails } from '../../../../../store/orders/order-details/orders.details.actions';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { openProductDetailModal } from '../../../../../store/products/products.actions';
import { useSellerFormattedDate } from '../../../../../hooks/use-datetime-formats';
import { ExpandableMessage } from './expandable-message';

import style from './style.module.less';

export const BodyRow = ({
  messages = emptyArray,
  messageType = '',
  isSelected = false,
  fulfillmentPlanId,
  orderNumber,
  problemDescription,
  orderDate,
  isWarning,
  sku,
  productId,
  name,
  showActionableColumns = true,
  showReadyToPickTableLayout = false,
  onRowSelected = noop,
  id = '',
  onRemoveRow,
  opensFromLabelQue = false,
  batchId = '',
  closeModal = noop,
}) => {
  const dispatch = useDispatch();
  const formattedOrderDate = useSellerFormattedDate(orderDate);
  const fulfillmentPlan = useSelector((state) => Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId })) || emptyObject;
  const { salesOrdersFulfilledIds = [] } = fulfillmentPlan;
  const salesOrderId = salesOrdersFulfilledIds[0];

  const [isExpanded, setIsExpanded] = useState(false);

  const onCheck = useCallback(
    (isChecked) => {
      onRowSelected(isChecked, { id });
    },
    [id, onRowSelected],
  );

  const onExpanded = useCallback(() => {
    setIsExpanded((_isExpanded) => !_isExpanded);
  }, []);

  const orderNumberClick = useCallback(() => {
    if (salesOrderId) {
      dispatch(goToOrderDetails({ salesOrderId, fulfillmentPlanId }));
      return;
    }

    closeModal();
    if (opensFromLabelQue) {
      dispatch(openShipmentDetailsFromLabelBatchStatusProcessing(fulfillmentPlanId, batchId));
      return;
    }
    dispatch(openShipmentDetailsFromLabelBatchStatus(fulfillmentPlanId, batchId));
  }, [batchId, closeModal, dispatch, fulfillmentPlanId, opensFromLabelQue, salesOrderId]);

  const skuClick = useCallback(() => {
    dispatch(openProductDetailModal(productId));
  }, [dispatch, productId]);

  const removeRow = useCallback(() => {
    onRemoveRow([fulfillmentPlanId]);
  }, [fulfillmentPlanId, onRemoveRow]);

  const rowClassNames = classnames(style['body-row'], { [style.selected]: isSelected }, { [style.expanded]: isExpanded });
  return (
    <div className={rowClassNames}>
      {showActionableColumns && (
        <div className={style['body-cell']}>
          <Checkbox checked={isSelected} onChange={onCheck} />
        </div>
      )}
      <div className={style['body-cell']}>
        <Button className={style['order-link']} onClick={orderNumberClick} theme='link'>
          <TruncatedText value={orderNumber || fulfillmentPlanId} />
        </Button>
      </div>
      {showReadyToPickTableLayout && problemDescription && (
        <>
          <div className={style['body-cell']}>
            <TruncatedText value={formattedOrderDate} />
          </div>
          <div className={style['body-cell']}>
            {isWarning ? (
              <FontAwesomeIcon
                className={classnames(style['icon-wrap'], style.warning, style.orderStatusIcon)}
                icon={faExclamationTriangle}
              />
            ) : (
              <FontAwesomeIcon className={classnames(style['icon-wrap'], style.error, style.orderStatusIcon)} icon={faExclamationCircle} />
            )}

            <TruncatedText value={problemDescription} />
          </div>
        </>
      )}
      {sku && (
        <div className={style['body-cell']}>
          <Button className={style['order-link']} onClick={skuClick} theme='link'>
            <TruncatedText value={sku} />
          </Button>
        </div>
      )}
      {showReadyToPickTableLayout && name && (
        <div className={style['body-cell']}>
          <TruncatedText value={name} />
        </div>
      )}
      {!showReadyToPickTableLayout && (
        <div className={classnames(style['body-cell'], style['error-warn-cell'])}>
          {messages &&
            messages.map((msg) => {
              return (
                <ExpandableMessage key={msg} isExpanded={isExpanded} message={msg} messageType={messageType} onExpanded={onExpanded} />
              );
            })}
        </div>
      )}
      {showActionableColumns && (
        <div className={style['body-cell']}>
          <Button className={style['remove-button']} onClick={removeRow} theme='link'>
            <FormattedMessage defaultMessage='Remove' description='batchProcessingModal.removeRow' id='0EqU/Q' />
          </Button>
        </div>
      )}
    </div>
  );
};

BodyRow.propTypes = {
  messages: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, MessageDescriptorPropType])),
  messageType: PropTypes.oneOf(['error', 'warning', 'activeLabelWarning']),
  isSelected: PropTypes.bool,
  onRowSelected: PropTypes.func,
  fulfillmentPlanId: PropTypes.string,
  showActionableColumns: PropTypes.bool,
  showReadyToPickTableLayout: PropTypes.bool,
  orderNumber: PropTypes.string,
  orderStatus: PropTypes.string,
  onRemoveRow: PropTypes.func.isRequired,
  id: PropTypes.string,
  closeModal: PropTypes.func,
  opensFromLabelQue: PropTypes.bool,
  batchId: PropTypes.string,
};
